import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmtB } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useCompanies } from '../lib/queries';
import {
  useConsolidatedBalanceSheet,
  useConsolidatedNci,
  useConsolidationEliminations,
  useConsolidationFinancials,
  useConsolidationGroups,
} from '../lib/queries/consolidation';

// =============================================================================
// Consolidation — uses the seeded companies list (DCC + subs) for the group
// structure card; every other tab renders an empty-state until consolidation
// entries / aggregated financials are wired into a later phase.
//
// The period selector and 6-tab layout are preserved (UI fidelity rule).
// =============================================================================

type TabKey = 'structure' | 'ic_balance' | 'ic_pl' | 'unreal' | 'elim' | 'consol';

const CO_TONE: Record<string, string> = {
  DCC: '#C00000',
  TTOP: '#203764',
  RCI: '#7030A0',
};

const SUB_CODES: readonly string[] = ['TTOP', 'RCI'];

export const Route = createFileRoute('/consol')({
  component: ConsolPage,
});

function ConsolPage() {
  const [tab, setTab] = useState<TabKey>('structure');
  const [showRun, setShowRun] = useState(false);

  // Period selector — defaults to current UTC YYYYMM.
  const now = new Date();
  const currentPeriod = `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  const todayIso = now.toISOString().slice(0, 10);
  const [period, setPeriod] = useState<string>(currentPeriod);

  // Live data — companies seed (DCC/TTOP/RCI) + the first registered group.
  const companiesQ = useCompanies();
  const companies = companiesQ.data ?? [];
  const parent = companies.find((c) => c.code === 'DCC');
  const subs = companies.filter((c) => SUB_CODES.includes(c.code));

  const groupsQuery = useConsolidationGroups();
  const liveGroupId = groupsQuery.data?.[0]?.id;
  const financialsQ = useConsolidationFinancials(liveGroupId, period);
  const balanceSheetQ = useConsolidatedBalanceSheet(liveGroupId, todayIso);
  const nciQ = useConsolidatedNci(liveGroupId, todayIso);
  const eliminationsQ = useConsolidationEliminations(liveGroupId, period);

  const eliminations = eliminationsQ.data ?? [];

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['structure', 'Combined Trial Balance'],
    ['ic_balance', 'Intercompany Balance'],
    ['ic_pl', 'Intercompany P&L'],
    ['unreal', 'Unrealized Profit'],
    ['elim', `Elimination JV${eliminations.length > 0 ? ` (${eliminations.length})` : ''}`],
    ['consol', 'งบการเงินรวม (Consolidated FS)'],
  ];

  // KPIs scrape the TOTAL rows from the API response. Until the group is
  // registered or there are no postings, all three resolve to 0.
  const totalAsset = useMemo(() => {
    const rows = balanceSheetQ.data?.rows ?? [];
    const totalRow = rows.find((r) => r.kind === 'TOTAL' && /asset|สินทรัพย์/i.test(r.label));
    return totalRow ? Number(totalRow.consolidated) : 0;
  }, [balanceSheetQ.data]);

  const groupProfit = useMemo(() => {
    const rows = financialsQ.data?.rows ?? [];
    let revenue = 0;
    let expense = 0;
    for (const r of rows) {
      const amt = Number(r.combined);
      if (r.accountType === 'REVENUE') revenue += amt;
      if (r.accountType === 'EXPENSE') expense += amt;
    }
    return revenue - expense;
  }, [financialsQ.data]);

  const nciTotal = useMemo(() => {
    if (!nciQ.data) return 0;
    return Number(nciQ.data.totalNci ?? 0);
  }, [nciQ.data]);

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'งบการเงินรวม']}
        title="งบการเงินรวม (Consolidated FS) — กลุ่ม DCC · TFRS 10"
        actions={
          <>
            <select className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value={currentPeriod}>งวด {currentPeriod}</option>
              {period !== currentPeriod ? <option value={period}>{period}</option> : null}
            </select>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire export */
              }}
            >
              <Icon name="download" size={14} /> Export Workbook
            </button>
            <button className="btn primary" onClick={() => setShowRun(true)}>
              <Icon name="refresh" size={14} /> Run Consolidation
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            สินทรัพย์รวม (Consolidated)
          </div>
          <div className="value tnum">
            {fmtB(totalAsset)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">หลัง Eliminate</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            กำไรรวมส่วนของกลุ่ม
          </div>
          <div className="value tnum">
            {fmtB(groupProfit)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">หลังหัก NCI</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: '#7030A0' }} />
            NCI
          </div>
          <div className="value tnum">
            {fmtB(nciTotal)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{subs.length} บริษัทย่อย</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span
              className="dot"
              style={{ background: liveGroupId ? 'var(--positive)' : 'var(--warning)' }}
            />
            สถานะกลุ่ม
          </div>
          <div className="value tnum">
            {liveGroupId ? 1 : 0} <span className="unit">กลุ่ม</span>
          </div>
          <div className="trend faint">
            {liveGroupId ? 'พร้อม Consolidate' : 'ยังไม่ได้ตั้งค่ากลุ่ม'}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>โครงสร้างกลุ่ม</h3>
        </div>
        <div className="card-body">
          {parent ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 18,
                alignItems: 'start',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: CO_TONE[parent.code] ?? 'var(--brand)',
                    color: 'white',
                    display: 'grid',
                    placeItems: 'center',
                    margin: '0 auto',
                    fontSize: 24,
                    fontWeight: 700,
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {parent.code}
                </div>
                <div style={{ fontWeight: 700, marginTop: 8 }}>บริษัทแม่ (Parent)</div>
                <div className="meta">{parent.nameTh}</div>
              </div>
              {subs.length === 0 ? (
                <div
                  className="muted"
                  style={{ gridColumn: 'span 2', padding: 32, textAlign: 'center' }}
                >
                  ยังไม่มีบริษัทย่อยในระบบ
                </div>
              ) : (
                subs.map((s) => (
                  <div key={s.id} style={{ textAlign: 'center' }}>
                    <div style={{ height: 24, position: 'relative', marginBottom: 6 }}>
                      <div
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: 0,
                          width: 1,
                          height: 24,
                          background: 'var(--border-strong)',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: CO_TONE[s.code] ?? '#52525b',
                        color: 'white',
                        display: 'grid',
                        placeItems: 'center',
                        margin: '0 auto',
                        fontSize: 18,
                        fontWeight: 700,
                        fontFamily: 'var(--font-display)',
                      }}
                    >
                      {s.code}
                    </div>
                    <div style={{ fontWeight: 700, marginTop: 8 }}>{s.code} (Subsidiary)</div>
                    <div className="meta">{s.nameTh}</div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
              ยังไม่มีข้อมูลบริษัท
            </div>
          )}
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'structure' && <EmptyTab label="Combined Trial Balance" hasGroup={!!liveGroupId} />}
      {tab === 'ic_balance' && <EmptyTab label="Intercompany Balance" hasGroup={!!liveGroupId} />}
      {tab === 'ic_pl' && <EmptyTab label="Intercompany P&L" hasGroup={!!liveGroupId} />}
      {tab === 'unreal' && <EmptyTab label="Unrealized Profit" hasGroup={!!liveGroupId} />}
      {tab === 'elim' && <EliminationTab eliminations={eliminations} hasGroup={!!liveGroupId} />}
      {tab === 'consol' && <EmptyTab label="งบการเงินรวม" hasGroup={!!liveGroupId} />}

      {showRun && <RunConsolModal onClose={() => setShowRun(false)} />}
    </div>
  );
}

function EmptyTab({ label, hasGroup }: { label: string; hasGroup: boolean }) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      </div>
      <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
        {hasGroup
          ? 'ยังไม่มีข้อมูลสำหรับช่วงเวลานี้'
          : 'ยังไม่ได้ตั้งค่ากลุ่ม Consolidation — โปรดสร้างกลุ่มก่อน'}
      </div>
    </div>
  );
}

interface ElimEntryRow {
  id: string;
  entryType?: string;
  description?: string;
  status?: string;
}

function EliminationTab({
  eliminations,
  hasGroup,
}: {
  eliminations: readonly ElimEntryRow[];
  hasGroup: boolean;
}) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          Elimination JV — {eliminations.length} ใบ
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>เลขที่</th>
            <th>ประเภท</th>
            <th>รายละเอียด</th>
            <th>สถานะ</th>
          </tr>
        </thead>
        <tbody>
          {eliminations.length === 0 ? (
            <tr>
              <td colSpan={4} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                {hasGroup
                  ? 'ยังไม่มีรายการ Elimination — กดปุ่ม "+ เพิ่ม Elimination" เพื่อสร้างรายการแรก'
                  : 'ยังไม่ได้ตั้งค่ากลุ่ม Consolidation'}
              </td>
            </tr>
          ) : (
            eliminations.map((e) => (
              <tr key={e.id}>
                <td className="code">
                  <b>{e.id.slice(0, 8)}</b>
                </td>
                <td>{e.entryType ?? '—'}</td>
                <td>{e.description ?? '—'}</td>
                <td>{e.status ?? '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function RunConsolModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{ margin: 0, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            Run Consolidation
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px' }}>
          <div className="muted" style={{ padding: 16, textAlign: 'center' }}>
            ยังไม่มีข้อมูลสำหรับการ Consolidate ในงวดนี้
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
}
